import React, { useState, useEffect } from "react";
import FooterDrawer from "@/components/ui/atoms/FooterDrawer";
import { useNavigate, useParams } from "react-router-dom";
import { isValidEmail, isValidMobile } from "@/utils";
import MDialCode from "@/components/ui/molecules/MDialCode";
import { dialCodeOptions } from "@/utils/countryCodes";
import { useDialCode } from "@/hooks/useDialCode";
import { MButton } from "@/components/ui/atoms/MButtons";
import MInputField from "@/components/ui/atoms/MInputField";
import { useRegister } from "@/hooks/useRegister";
import { useAuthUiStrings } from "@/utils/auth-utils";
import { useAuthStepEffects } from "@/hooks/useAuthStepEffects";
import MOnboardingHeader from "@/components/ui/organisms/MOnboardingHeader";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useRegisterContext } from "@/context/RegisterContext";
import { memberWhitelistService } from "@/services/member-whiltelist.service";
import { useToast } from '@/components/ui/atoms/MToastProvider';
import { savePreferredLoginMethod } from '@/utils/storage-utils';
import { useKeyboardBottomPadding } from '@/hooks/useKeyboardBottomPadding';
import { useFixMobileKeyboard } from '@/hooks/useFixMobileKeyboard';

const SignupInput: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const { showToast } = useToast();
  const doCheckWhitelist = false;
  const { credentials, updateCredentials } = useRegisterContext();
  const [readonlyInput, setReadonlyInput] = useState(false);
  const [showMoreInfo, setShowMoreInfo] = useState(false);
  const [inviteToken, setInviteToken] = useState<string | undefined>(undefined);
  const inviteFlow = Boolean(inviteToken);
  const [prefilledValue, setPrefilledValue] = useState<string | undefined>(
    undefined
  );
  const [showDialCodeDrawer, setShowDialCodeDrawer] = useState(false);
  const [invitationState, setInvitationState] = useState<
    { firstName?: string; lastName?: string } | undefined
  >(undefined);
  const navigate = useNavigate();
  const { mode } = useParams<{ mode: string }>();
  const isLogin = location.state?.isLogin || false;
  // Remove local inputValue state; use inputValue from useRegister
  const { dialCode, setDialCode } = useDialCode("+91");

  const [otpValue, setOtpValue] = useState("");
  const [step, setStep] = useState<"input" | "otp">("input");
  const [error, setError] = useState<string | null>(null);

  const {
    sendOtp,
    verifyOtp,
    loading,
    inputValue,
    setInputValue,
    setOtp,
    setOtpSent,
    error: registerError,
    setVerifyType,
  } = useRegister(prefilledValue, inviteFlow, inviteToken, false);

  // Adapter for setVerifyType to accept 'sms' | 'email' and convert to 'mobile' | 'email'
  const setVerifyTypeAdapter = (v: "sms" | "email") =>
    setVerifyType(v === "sms" ? "mobile" : "email");

  // Use unified hook for all OTP/resend/CTA logic
  const { resendTimer, handleResend, handleCta } =
    useAuthStepEffects({
      mode,
      setVerifyType: setVerifyTypeAdapter,
      setStep,
      setOtpSent,
      setError,
      setOtpValue,
      setInputValue,
      setOtp,
      step,
      registerError,
      sendOtp,
      verifyOtp,
      inputValue,
      dialCode,
      otpValue,
      navigate,
      isSignup: !isLogin,
      onOtpSuccess: (params) => {
        savePreferredLoginMethod(mode === 'mobile' ? 'mobile' : 'email');

        if (isLogin) {
          navigate("/dashboard");
        } else if (mode === "mobile") {
          navigate("/register/signupconfirm", {
            state: {
              mobile: inputValue,
              dialCode,
              inviteToken,
              ...invitationState,
            },
          });
        } else {
          navigate("/register/signupconfirm", {
            state: { email: inputValue, inviteToken, ...invitationState },
          });
        }
      },
    });

  // Prefill and lock input if coming from consent page
  useEffect(() => {
    // Defensive: check for state from location (consent page)
    const state = location?.state as
      | {
        email?: string;
        mobile?: string;
        inviteToken?: string;
        dialCode?: string;
        firstName?: string;
        lastName?: string;
      }
      | undefined;
    if (state && (state.email || state.mobile) && state.inviteToken) {
      setInvitationState({
        firstName: state.firstName,
        lastName: state.lastName,
      });
      setInviteToken(state.inviteToken);
      if (state.email) {
        setInputValue(state.email);
        setPrefilledValue(state.email);
        setVerifyType("email");
        setReadonlyInput(true);
      } else if (state.mobile) {
        setInputValue(state.mobile);
        setPrefilledValue(state.mobile);
        setVerifyType("mobile");
        setReadonlyInput(true);
      }
      if (
        typeof state.dialCode === "string" &&
        state.dialCode.trim().length > 0
      )
        setDialCode(state.dialCode);
      setStep("input");
      setOtpSent(false);
    } else {
      setReadonlyInput(false);
      setInviteToken(undefined);
      setPrefilledValue(undefined);
    }
  }, [
    location,
    setInputValue,
    setVerifyType,
    setDialCode,
    setStep,
    setOtpSent,
  ]);

  // Ensure verifyType matches mode from URL (only if not readonly/consent)
  useEffect(() => {
    if (!readonlyInput) {
      if (mode === "mobile") {
        setVerifyTypeAdapter("sms");
      } else {
        setVerifyTypeAdapter("email");
      }
    }
  }, [mode, setVerifyTypeAdapter, readonlyInput]);

  // --- removed local handleResend, now using from hook
  // const handleResend = async () => {
  //   ...removed...
  // };

  useEffect(() => {
    if (registerError) setError(registerError);
  }, [registerError]);

  // Store inputValue in context whenever it changes (for email/mobile)
  useEffect(() => {
    if (inputValue && !readonlyInput) {
      if (mode === "mobile") {
        updateCredentials({ mobile: inputValue, countryCode: dialCode });
      } else {
        updateCredentials({ email: inputValue });
      }
    }
  }, [inputValue, mode, dialCode, readonlyInput]);

  // Restore inputValue from context on component mount
  useEffect(() => {
    if (!readonlyInput && !prefilledValue) {
      if (mode === "mobile" && credentials.mobile) {
        setInputValue(credentials.mobile);
        if (credentials.countryCode) {
          setDialCode(credentials.countryCode);
        }
      } else if (mode === "email" && credentials.email) {
        setInputValue(credentials.email);
      }
    }
  }, [mode]);

  // Use shared hook for UI strings and input types
  const { heading, placeholder, inputType } = useAuthUiStrings(
    step,
    mode === "mobile" ? "mobile" : "email"
  );

  const keyboardBottomPadding = useKeyboardBottomPadding();
  useFixMobileKeyboard();

  // Handle input focus to prevent keyboard jump
  const handleInputFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    // Small delay to let keyboard animation start
    setTimeout(() => {
      e.target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 100);
  };

  const checkWhitelist = async (mode: "email" | "mobile"): Promise<boolean> => {
    if (!doCheckWhitelist) return true;
    try {
      let isWhitelisted = false;
      if (mode === "email") {
        isWhitelisted = await memberWhitelistService.checkEmailWhitelist(inputValue);
      } else {
        isWhitelisted = await memberWhitelistService.checkMobileWhitelist(inputValue);
      }
      if (!isWhitelisted) {
        showToast({ message: t("onboarding.whitelist.errorCheckingWhitelist"), variant: "default", duration: 5000 });
      }
      return isWhitelisted;
    } catch (error) {
      console.error("Error checking whitelist:", error);
      showToast({ message: t("onboarding.whitelist.errorCheckingWhitelist"), variant: "default", duration: 5000 });
      return false;
    }
  };

  const processSendCode = async (mode: "email" | "mobile") => {
    const isWhitelisted = await checkWhitelist(mode);
    if (isWhitelisted) {
      handleCta();
    }
  }

  return (
    <React.Fragment>
      <FooterDrawer
        open={showMoreInfo}
        onClose={() => setShowMoreInfo(false)}
        title={t("common.moreInfo")}
        showClose
      >
        <div className="flex flex-col items-center justify-center px-6 py-8">
          <p className="font-medium mb-2" style={{ fontSize: 16 }}>
            {t('auth.onboarding.signupForm.moreInfoText')}
          </p>
        </div>
      </FooterDrawer>
      <div className="w-screen min-h-screen bg-white flex flex-col">
        {/* Onboarding Header with gradient, back/exit, and step indicators or icon */}
        <MOnboardingHeader
          currentStep={isLogin ? undefined : 2}
          totalSteps={isLogin ? undefined : 3}
          onBack={() => navigate(-1)}
          onExit={() => navigate('/')}
          showIcon={isLogin ? (mode === 'mobile' ? 'phone' : 'email') : undefined}
        />

        <div
          className="flex-1 min-h-0 overflow-y-auto px-6 mt-8 pb-6"
        >
          {step === "input" && (
            <>
              {/* Title */}
              <h1 className="text-[24px] font-bold text-[#36116D] mb-2">
                {mode === "mobile" ? t('auth.onboarding.signupForm.titleMobile') : t('auth.onboarding.signupForm.titleEmail')}
              </h1>
              <p className="text-[#474747] text-[16px] mb-8">
                {t('auth.onboarding.signupForm.subtitle')}
              </p>

              {/* Input Section */}
              {mode === "mobile" ? (
                <>
                  <div className="flex items-center gap-3 mb-6">
                    <div
                      className="flex items-center gap-2 px-4 py-3 border-2 border-[#6BA2EA] rounded-lg"
                      style={{
                        opacity: readonlyInput ? 0.5 : 1,
                      }}
                    >
                      {(() => {
                        const codeObj = dialCodeOptions.find(
                          (opt) => opt.value === dialCode
                        );
                        return codeObj ? (
                          <img
                            src={`https://flagcdn.com/w40/${codeObj.countryCode.toLowerCase()}.png`}
                            alt={codeObj.countryCode}
                            className="w-6 h-4"
                          />
                        ) : null;
                      })()}
                      <span className="font-semibold text-gray-900">{dialCode}</span>
                    </div>
                    <MInputField
                      size="xl"
                      variant="Outlined"
                      state="Default"
                      inputMode="numeric"
                      type="number"
                      maxLength={10}
                      placeholder={t('auth.onboarding.signupForm.mobilePlaceholder')}
                      value={inputValue}
                      onChange={(e) => setInputValue(e.target.value)}
                      onFocus={handleInputFocus}
                      className="flex-1"
                      readOnly={readonlyInput}
                    />
                  </div>
                  <div className="text-center mb-8">
                    <button
                      className="text-[#45168C] font-semibold text-sm"
                      onClick={() => navigate("/register/SignupForm/email")}
                    >
                      {t('auth.onboarding.signupForm.useEmailInstead')}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <MInputField
                    size="xl"
                    variant="Outlined"
                    type={inputType}
                    placeholder={t('auth.onboarding.signupForm.emailPlaceholder')}
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onFocus={handleInputFocus}
                    className="mb-6"
                    readOnly={readonlyInput}
                    state="Default"
                  />

                  <div className="text-center mb-8">
                    <button
                      className="text-[#45168C] font-semibold text-sm"
                      onClick={() => navigate("/register/SignupForm/mobile")}
                    >
                      {t('auth.onboarding.signupForm.useMobileInstead')}
                    </button>
                  </div>
                </>
              )}

              {/* Button inside scrollable area */}
              <div className="mt-6 confirm-footer">
                <MButton
                  variant="filled"
                  size="lg"
                  className="w-full"
                  onClick={() => mode === "mobile" ? processSendCode('mobile') : processSendCode('email')}
                  disabled={
                    !inputValue ||
                    loading ||
                    (mode === "mobile" ? !isValidMobile(inputValue) : !isValidEmail(inputValue))
                  }
                >
                  {t('auth.onboarding.signupForm.confirm')}
                </MButton>
              </div>
            </>
          )}
          {step === "otp" && (
            <>
              {/* Title */}
              <h1 className="text-[24px] font-bold text-purple-900 mb-2">
                {mode === "mobile" ? t('auth.onboarding.signupForm.titleMobile') : t('auth.onboarding.signupForm.titleEmail')}
              </h1>
              <p className="otpText mb-8">
                {t('auth.onboarding.signupForm.otpSent', {
                  contact: mode === "mobile" ? `${dialCode} ${inputValue}` : inputValue
                })}
              </p>

              {/* OTP Input */}
              <MInputField
                size="xl"
                variant="Outlined"
                state="Default"
                inputMode="numeric"
                type="number"
                maxLength={6}
                placeholder={t('auth.onboarding.signupForm.otpPlaceholder')}
                value={otpValue}
                autoFocus
                onFocus={handleInputFocus}
                onChange={(e) => {
                  const val = e.target.value.replace(/\D/g, "");
                  setOtpValue(val);
                }}
                className="mb-4"
              />

              {/* Resend code link */}
              <div className="flex items-center gap-2 mb-4">
                <span className="text-sm text-gray-600">{t('auth.onboarding.signupForm.didntGetCode')}</span>
                <button
                  className="text-sm  font-semibold disabled:opacity-50"
                  style={{color : "#B23F83"}}
                  disabled={resendTimer > 0 || loading}
                  onClick={() => {
                    if (resendTimer === 0 && !loading) handleResend();
                  }}
                >
                  {t('auth.onboarding.signupForm.resendCode')}
                </button>
              </div>

              {resendTimer > 0 && (
                <p className="text-sm text-gray-500 mb-8">
                  {t('auth.onboarding.signupForm.resendIn', { seconds: resendTimer.toString().padStart(2, "0") })}
                </p>
              )}

              {/* Button inside scrollable area */}
              <div className="mt-6 confirm-footer">
                <MButton
                  variant="filled"
                  size="lg"
                  className="w-full"
                  onClick={handleCta}
                  disabled={otpValue.length !== 6 || loading}
                >
                  {t('auth.onboarding.signupForm.confirm')}
                </MButton>
              </div>
            </>
          )}
        </div>
      </div>
    </React.Fragment>
  );
};

export default SignupInput;
